/* eslint-disable @typescript-eslint/no-empty-function */
import type { FC, PropsWithChildren } from 'react';

import { getCookie } from 'cookies-next';
import { useRouter } from 'next/compat/router';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import {
  CHE,
  COCO,
  IMANRUT,
  OPTANON_ALERT_BOX_CLOSED,
  OPTANON_CONSENT,
  RSU,
} from '../../constants/cookies';

type CookieContextType = {
  /**
   * `Identify Me As Non-Real-User-Traffic`
   *
   * can be also set as query param `?imanrut=true'
   */
  imanrut: boolean;
  /**
   * `Randomly Selected UserID`
   *
   * UUID without dashes
   */
  rsu: string | undefined;
  /**
   * Hashed UserID for Authenticated users
   */
  che: string | undefined;
  /**
   * `Consent Cookie`
   */
  cocoCookie?: string | boolean | null;
  /**
   * setter for `Consent Cookie`
   */
  setCocoCookie: (value?: string | boolean | null) => void;
  /**
   * `Consent OneTrust Cookie`
   */
  oneTrustCookie?: string | boolean | null;
  /**
   * setter for `Consent OneTrust Cookie`
   */
  setOneTrustCookie: (value?: string | boolean | null) => void;
  /**
   * OneTrust only sets this once the user has actually closed the banner (accept/reject/save
   * preferences). Unlike `oneTrustCookie`, it is never written ahead of an explicit choice, so
   * it's the reliable signal for "has the user decided yet".
   */
  optanonAlertBoxClosedCookie?: string | boolean | null;
  setHasInteracted: () => void;
  hasInteracted: number;
};

const CookieContext = createContext<CookieContextType>({
  rsu: '',
  che: '',
  imanrut: false,
  cocoCookie: undefined,
  setCocoCookie: () => {},
  oneTrustCookie: undefined,
  setOneTrustCookie: () => {},
  optanonAlertBoxClosedCookie: undefined,
  setHasInteracted: () => {},
  hasInteracted: 0,
});

export const useCookieContext = () => useContext(CookieContext);

export const CookieProvider: FC<PropsWithChildren> = ({ children }) => {
  const router = useRouter();
  const imanrutValue = router?.query?.imanrut || getCookie(IMANRUT);
  const [cocoCookie, setCocoCookie] = useState(getCookie(COCO));
  const [oneTrustCookie, setOneTrustCookie] = useState(getCookie(OPTANON_CONSENT));
  const [optanonAlertBoxClosedCookie, setOptanonAlertBoxClosedCookie] = useState(
    getCookie(OPTANON_ALERT_BOX_CLOSED)
  );
  const rsu = getCookie(RSU)?.toString();
  const che = getCookie(CHE)?.toString();
  const [hasInteracted, setHasInteracted] = useState(0);

  const incrementHasInteracted = useCallback(() => {
    setHasInteracted(prev => prev + 1);
  }, []);

  let imanrut = false;

  if (imanrutValue) {
    if (typeof imanrutValue === 'boolean') {
      imanrut = imanrutValue;
    } else if (Array.isArray(imanrutValue)) {
      imanrut = JSON.parse(imanrutValue?.at(0) || 'false');
    } else {
      imanrut = JSON.parse(imanrutValue);
    }
  }

  useEffect(() => {
    const coco = getCookie(COCO) as string;
    const oneTrust = getCookie(OPTANON_CONSENT) as string;
    const optanonAlertBoxClosed = getCookie(OPTANON_ALERT_BOX_CLOSED) as string;

    setCocoCookie(coco as string);
    setOneTrustCookie(oneTrust as string);
    setOptanonAlertBoxClosedCookie(optanonAlertBoxClosed as string);
  }, [hasInteracted]);

  const value = useMemo(
    () => ({
      rsu,
      che,
      imanrut,
      cocoCookie,
      setCocoCookie,
      oneTrustCookie,
      setOneTrustCookie,
      optanonAlertBoxClosedCookie,
      setHasInteracted: incrementHasInteracted,
      hasInteracted,
    }),
    [
      cocoCookie,
      imanrut,
      rsu,
      che,
      oneTrustCookie,
      optanonAlertBoxClosedCookie,
      hasInteracted,
      incrementHasInteracted,
    ]
  );

  return <CookieContext.Provider value={value}>{children}</CookieContext.Provider>;
};
